import React, { useState, useMemo, useEffect, memo } from 'react';
import {
  Box,
  Button,
  Collapse,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const MhdNestedSelect = ({ label, value, override, warning, error, onChange, disabled, choices }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openGroup, setOpenGroup] = useState(null);

  const groupedChoices = useMemo(() => {
    const result = [];
    let currentGroup = null;
    choices.forEach((item) => {
      if (item.group) {
        currentGroup = item.group;
        result.push({ type: 'group', label: item.group });
      } else {
        result.push({ type: 'item', ...item, group: currentGroup });
      }
    });
    return result;
  }, [choices]);

  const selectedChoice = useMemo(
    () => groupedChoices.find((c) => c.type === 'item' && c.value === value),
    [groupedChoices, value]
  );

  const selectedLabel = selectedChoice
    ? `${selectedChoice.group} | ${selectedChoice.label}`
    : 'Select...';

  useEffect(() => {
    if (anchorEl && selectedChoice?.group) {
      setOpenGroup(selectedChoice.group);
    }
  }, [anchorEl, selectedChoice]);

  const handleToggleGroup = (group) => {
    setOpenGroup((prev) => (prev === group ? null : group));
  };

  const handleSelect = (val) => {
    onChange(val);
    setAnchorEl(null);
  };

  return (
    <>
      <InputLabel variant={error ? 'error' : 'primary'}>{label}</InputLabel>

      <Tooltip
        title={warning}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: { backgroundColor: theme.palette.error.main, color: 'white' }
          },
          arrow: { sx: { color: theme.palette.error.main } }
        }}
      >
        <Button
          variant="contained"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          color="secondary"
          disabled={disabled}
          sx={{
            height: '30px',
            width: '100%',
            backgroundColor: anchorEl ? '#9C9EB7' : 'primary',
            boxShadow: anchorEl
              ? 'inset 3px 3px 3px rgba(0,0,0,0.3), inset -1px -1px 2px rgba(0,0,0,0.1)'
              : '3px 3px 3px rgba(0,0,0,0.3)',
            '&:hover': {
              backgroundColor: anchorEl ? '#8a8eb6' : 'primary',
              boxShadow: anchorEl
                ? 'inset 3px 3px 3px rgba(0,0,0,0.3), inset -1px -1px 2px rgba(0,0,0,0.1)'
                : '3px 3px 3px rgba(0,0,0,0.3)'
            }
          }}
        >
          <Typography fontSize={10} fontWeight='bold'>
            {selectedLabel}
          </Typography>
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ sx: { p: 0 } }}
        PaperProps={{ sx: { borderRadius: 1, width: 200, maxHeight: 400 } }}
      >
        <List dense disablePadding>
          {groupedChoices.map((choice, index) => {
            if (choice.type === 'group') {
              const isOpen = openGroup === choice.label;
              return (
                <ListItemButton
                  key={`group-${index}`}
                  onClick={() => handleToggleGroup(choice.label)}
                  sx={{
                    backgroundColor: isOpen ? 'lightgrey' : 'grey',
                    height: 35,
                    color: isOpen ? 'inherit' : 'white',
                    '&:hover': { color: 'inherit' }
                  }}
                >
                  <ListItemText primary={choice.label} />
                  {isOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              );
            } else {
              const isVisible = openGroup === choice.group;
              return (
                <Collapse key={`item-${index}`} in={isVisible} timeout="auto" unmountOnExit>
                  <MenuItem
                    onClick={() => handleSelect(choice.value)}
                    disabled={choice.disabled}
                    sx={{ alignItems: 'start', minHeight: choice.secondaryLabel ? 50 : 30 }}
                  >
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{
                          color: choice.value === value ? 'white' : 'inherit',
                          fontWeight: choice.value === value ? 'bolder' : 'inherit',
                          textShadow: choice.value === value ? `1px 1px 3px black, -1px -1px 3px black, 1px -1px 3px black, -1px 1px 3px black` : 'inherit'
                      }}
                      >
                        {choice.label}
                      </Typography>
                      {choice.secondaryLabel && (
                        <Typography variant="body2" color="textSecondary">
                          {choice.secondaryLabel}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                </Collapse>
              );
            }
          })}
        </List>
      </Menu>

      {(override || warning) && (
        <Typography variant='caption' color='error'>
          Modified: {choices.find((item) => item.value === override)?.label ?? 'None'}
        </Typography>
      )}
    </>
  );
};

export default memo(MhdNestedSelect);
